import requestService from '@/services/request'
import { createSelector, createSlice } from '@reduxjs/toolkit'
import { transformArrayIntoMap } from '@/util/mapArray'
import constants from '@/constants'
import { QUESTION_STATUS } from '@/constants/marketBackendEnums'
import { getMarketType } from '@/util/currencies'
import { toastService } from '@/api/toast'
import limitOrderAPI from '@/api/limitOrder'
import ErrorService from '@/services/errors/ErrorService'
import { i18n } from '@/locale/i18n'

/**
 * Outcome prices are keyed by currency (`{ OOM: 0.53 }`). The real-time `price-change` payload can
 * serialize the values as strings, so they are coerced before reaching the store.
 */
const toNumericPriceMap = price => {
  if (!price || typeof price !== 'object') return price
  return Object.fromEntries(
    Object.entries(price).map(([currency, value]) => [currency, value == null ? value : Number(value)])
  )
}

const initialState = {
  pool: { data: {}, status: {}, error: {} },
  feed: {
    status: constants.IDLE,
    data: [],
    pagination: {},
    error: null,
    marketType: null,
    queryString: '',
    currentCategory: '',
    latestRequestId: 0,
  },
  categories: { status: {}, data: {}, pagination: {}, error: {} },
  tags: { status: {}, data: {}, pagination: {}, error: {} },
  related_markets: [],
  hot_markets: [],
  highlighted_markets: [],
  orderBook: {
    data: {}, // { [outcomeId]: { [currencyType]: { [position]: { ask: [], bid: [], status: constants.IDLE, error: null } } } }
    currentMarketId: null, // to track when market changes
  },
  userOrders: {
    data: {}, // { [marketId]: { orders: [], isLoading: false, error: null } }
    status: constants.IDLE,
    error: null,
  },
}

// Tracks the in-flight feed request so stale, out-of-order responses can be
// aborted/ignored when the user navigates between categories faster than the
// previous request resolves.
let feedRequestCounter = 0
let currentFeedAbortController = null

// Reducers / Slices
const mergeObjects = (obj1, obj2) => {
  const mergedObj = { ...obj1 } // Start with a shallow copy of obj1

  for (const id in obj2) {
    if (mergedObj.hasOwnProperty(id)) {
      // If the ID exists in both objects, merge the properties
      mergedObj[id] = { ...mergedObj[id], ...obj2[id] }
    } else {
      // If the ID is unique to obj2, add it to the merged object
      mergedObj[id] = { ...obj2[id] }
    }
  }

  return mergedObj
}
const markets = createSlice({
  name: 'markets',
  initialState,
  reducers: {
    resetAllMarkets: state => {
      state.feed = { ...initialState.feed }
      state.categories = { ...initialState.categories }
      state.tags = { ...initialState.tags }
      state.pool = { ...initialState.pool }
    },

    getFeedStart: (state, action) => {
      const { marketType, requestId } = action.payload
      state.feed.marketType = marketType
      state.feed.latestRequestId = requestId

      state.feed.status = constants.LOADING
    },

    getFeedSuccess: (state, action) => {
      const { results, pagination, queryString, currentCategory, replace, requestId } = action.payload
      if (requestId !== state.feed.latestRequestId) return
      const ids = results.map(el => el.id)
      const markets = transformArrayIntoMap(results)
      state.feed.queryString = queryString
      state.feed.currentCategory = currentCategory
      state.feed.status = constants.COMPLETE
      state.feed.pagination = pagination
      state.feed.data = replace ? ids : state.feed.data.concat(ids)
      state.pool.data = { ...state.pool.data, ...markets }
    },

    getFeedError: (state, action) => {
      const { requestId, ...error } = action.payload
      if (requestId !== state.feed.latestRequestId) return
      state.feed.error = error
      state.feed.status = constants.ERROR
    },

    removeFromFeed: (state, action) => {
      const marketId = action.payload
      state.feed.data = state.feed.data.filter(id => id !== marketId)
    },

    resetFeed: state => {
      state.feed = { ...initialState.feed, status: constants.IDLE, marketType: state.feed.marketType }
      state.pool = { ...initialState.pool }
    },

    resetCategory: (state, action) => {
      const { categoryId } = action.payload
      state.categories.data[categoryId] = []
      state.categories.status[categoryId] = constants.IDLE
      state.categories.pagination[categoryId] = {}
    },

    getMarketByIdStart: (state, action) => {
      const marketId = action.payload
      state.pool.status[marketId] = constants.LOADING
    },

    getMarketByIdSuccess: (state, action) => {
      const market = action.payload.data
      state.pool.data[market.id] = { ...market, complete: true }
      state.pool.status[market.id] = constants.COMPLETE
    },
    getRelatedMarketSuccess: (state, action) => {
      state.related_markets = action.payload.data
    },
    getHotMarketSuccess: (state, action) => {
      state.hot_markets = action.payload.data
    },

    getMarketByIdError: (state, action) => {
      const { marketId, error } = action.payload
      state.pool.status[marketId] = constants.ERROR
      state.pool.error[marketId] = error
    },

    updateMarketOutcomes: (state, action) => {
      const { marketId, data, activeCurrency } = action.payload
      const market = state.pool.data[marketId]
      if (!market) return

      const {
        markets: incomingMarkets,
        wagers_count_play_money,
        wagers_count_real_money,
        ...rest
      } = data

      const patch = {
        ...rest,
        ...(wagers_count_play_money != null ? { wagers_count: wagers_count_play_money } : {}),
        ...(wagers_count_real_money != null ? { wagers_count_canonical: wagers_count_real_money } : {}),
      }

      if (data.currency_mode === activeCurrency && incomingMarkets) {
        patch.outcomes = incomingMarkets.map(outcome => {
          const merged = {
            ...market.outcomes.find(o => o.id === outcome.id),
            ...outcome,
          }
          return 'price' in merged ? { ...merged, price: toNumericPriceMap(merged.price) } : merged
        })
      } else {
        patch.outcomes = market.outcomes
      }

      state.pool.data[marketId] = { ...market, ...patch }
    },

    updateMarketSportsEvent: (state, action) => {
      const { marketId, patch } = action.payload
      if (!patch) return

      const poolKey =
        state.pool.data[marketId] != null
          ? marketId
          : state.pool.data[Number(marketId)] != null
            ? Number(marketId)
            : state.pool.data[String(marketId)] != null
              ? String(marketId)
              : null

      const market = poolKey != null ? state.pool.data[poolKey] : null
      if (!market) {
        if (import.meta.env.DEV) {
          console.warn('[sports-pusher] market not in pool — UI will not update until it is loaded', marketId)
        }
        return
      }

      const isResolvedStatus = patch.status === QUESTION_STATUS.RESOLVED

      state.pool.data[poolKey] = {
        ...market,
        ...(patch.event_start_date !== undefined && { event_start_date: patch.event_start_date }),
        ...(patch.event_end_date !== undefined && { event_end_date: patch.event_end_date }),
        ...(patch.status !== undefined && { status: patch.status }),
        ...(patch.resolution !== undefined && { resolution: patch.resolution }),
        ...(patch.resolve_date !== undefined && { resolve_date: patch.resolve_date }),
        ...(patch.is_wagerable !== undefined &&
          !isResolvedStatus && { is_wagerable: patch.is_wagerable }),
        ...(patch.is_live !== undefined && !isResolvedStatus && { is_live: patch.is_live }),
        ...(isResolvedStatus && { is_live: false, is_wagerable: false }),
        ...(patch.event_extra_data && {
          event_extra_data: {
            ...(market.event_extra_data || {}),
            ...patch.event_extra_data,
          },
        }),
      }
    },

    updateMarket: (state, action) => {
      const { market } = action.payload
      const currentMarket = state.pool.data[market.id]

      if (!currentMarket) {
        state.pool.data[market.id] = { ...currentMarket, complete: currentMarket.complete }
      } else {
        state.pool.data[market.id] = { ...currentMarket, ...market, complete: currentMarket.complete }
      }
    },

    getMarketsByTagStart: (state, action) => {
      const { tag } = action.payload
      state.tags.status[tag] = constants.LOADING
    },

    getMarketsByTagSuccess: (state, action) => {
      const { tag, marketsList, pagination } = action.payload
      const markets = transformArrayIntoMap(marketsList)
      const marketIds = marketsList.map(market => market.id)

      if (!state.tags.data[tag]) {
        state.tags.data[tag] = []
      }

      state.pool.data = { ...state.pool.data, ...markets }
      state.tags.status[tag] = constants.COMPLETE
      state.tags.data[tag].push(...marketIds)
      state.tags.pagination[tag] = pagination
    },

    getMarketsByTagError: (state, action) => {
      const { tag, error } = action.payload
      state.tags.status[tag] = constants.ERROR
      state.tags.error[tag] = error
    },

    getMarketByCategoryIdStart: (state, action) => {
      const { categoryId } = action.payload
      state.categories.status[categoryId] = constants.LOADING
    },

    getMarketByCategoryIdSuccess: (state, action) => {
      const { categoryId, marketsList, pagination } = action.payload
      const markets = transformArrayIntoMap(marketsList)
      const marketIds = marketsList.map(market => market.id)

      if (!state.categories.data[categoryId]) {
        state.categories.data[categoryId] = []
      }

      state.pool.data = { ...state.pool.data, ...markets }
      state.categories.status[categoryId] = constants.COMPLETE
      state.categories.data[categoryId].push(...marketIds)
      state.categories.pagination[categoryId] = pagination
    },

    getMarketByCategoryIdError: (state, action) => {
      const { categoryId, error } = action.payload
      state.categories.status[categoryId] = constants.ERROR
      state.categories.error[categoryId] = error
    },

    addBetToMarket: (state, action) => {
      const { bet, marketId } = action.payload
      const market = state.pool && state.pool.data[marketId]
      if (market) {
        if (Array.isArray(market.wagers)) {
          market.wagers.unshift({ ...bet })
        } else {
          market.wagers = [{ ...bet }]
        }
      }
    },
    updateBetInMarket: (state, action) => {
      const { bet, marketId } = action.payload
      const market = state.pool && state.pool.data[marketId]
      if (market) {
        if (!Array.isArray(market.wagers)) market.wagers = []
        if (!market.wagers.find(w => w.id === bet.id)) market.wagers.unshift(bet)
        else
          market.wagers.forEach((w, i) => {
            if (w.id === bet.id) {
              market.wagers[i] = bet
            }
          })
      }
    },
    overwriteBetsInMarket: (state, action) => {
      const { marketId, bets, clear = false } = action.payload
      const market = state.pool.data[marketId]
      if (market) {
        if (clear) {
          // Clear existing wagers before setting new ones
          market.wagers = []
        }
        market.wagers = bets
      }
    },

    addMarketsToPool: (state, action) => {
      const { markets } = action.payload
      if (state.pool && state.pool.data) {
        const marketsMap = transformArrayIntoMap(markets)
        state.pool.data = mergeObjects(state.pool.data, marketsMap)
      }
    },

    highlightMarkets: (state, action) => {
      const { marketIds } = action.payload
      // Add markets to highlighted list
      state.highlighted_markets = [...new Set([...state.highlighted_markets, ...marketIds])]

      // Get markets that are not in the feed
      const marketsNotInFeed = marketIds.filter(id => !state.feed.data.includes(id))

      // Add new markets to the feed
      if (marketsNotInFeed.length > 0) {
        state.feed.data = [...marketsNotInFeed, ...state.feed.data]
      }

      // Move highlighted markets to the top of the feed
      const highlightedInFeed = state.feed.data.filter(id => marketIds.map(Number).includes(Number(id)))
      const otherInFeed = state.feed.data.filter(id => !marketIds.map(Number).includes(Number(id)))
      state.feed.data = [...highlightedInFeed, ...otherInFeed]
    },

    clearHighlightedMarkets: state => {
      state.highlighted_markets = []
    },

    orderBookFetchStart: (state, action) => {
      const { outcomeId, currency_mode, marketId, position } = action.payload

      // Only clear all orderBook data if we're explicitly switching from one market to another
      // in a single-market context. In multi-market contexts (like portfolio), we want to
      // preserve all orderbooks.
      //
      // Strategy: Only clear if we're switching markets AND we don't have orderbook data for
      // other outcomes. If we have data for other outcomes, we're in a multi-market context
      // (like portfolio) and should preserve all orderbooks.
      if (state.orderBook.currentMarketId !== null && state.orderBook.currentMarketId !== marketId) {
        // Check if we have orderbook data for other outcomes (not the one we're about to fetch)
        const hasDataForOtherOutcomes = Object.keys(state.orderBook.data).some(
          id => id !== outcomeId && state.orderBook.data[id] && Object.keys(state.orderBook.data[id]).length > 0
        )

        // Only clear if we're switching markets and don't have data for other outcomes
        // This preserves orderbooks in portfolio when loading more bets
        // (if we have data for other outcomes, we're in a multi-market context)
        if (!hasDataForOtherOutcomes) {
          state.orderBook.data = {}
        }
        // Always update currentMarketId to track the latest market
        state.orderBook.currentMarketId = marketId
      } else if (state.orderBook.currentMarketId === null) {
        // Set currentMarketId if it's null (first market being viewed)
        state.orderBook.currentMarketId = marketId
      }

      if (!state.orderBook.data[outcomeId]) state.orderBook.data[outcomeId] = {}
      if (!state.orderBook.data[outcomeId][currency_mode]) state.orderBook.data[outcomeId][currency_mode] = {}

      state.orderBook.data[outcomeId][currency_mode][position] = {
        ...(state.orderBook.data[outcomeId][currency_mode][position] || {}),
        status: constants.LOADING,
        error: null,
      }
    },
    orderBookFetchSuccess: (state, action) => {
      const { outcomeId, currency_mode, ask, bid, position, last_price } = action.payload
      if (!state.orderBook.data[outcomeId]) state.orderBook.data[outcomeId] = {}
      if (!state.orderBook.data[outcomeId][currency_mode]) state.orderBook.data[outcomeId][currency_mode] = {}
      state.orderBook.data[outcomeId][currency_mode][position] = {
        ask: ask || [],
        bid: bid || [],
        last_price: last_price ?? null,
        status: constants.COMPLETE,
        error: null,
      }
    },
    orderBookFetchError: (state, action) => {
      const { outcomeId, currency_mode, error, position } = action.payload
      if (!state.orderBook.data[outcomeId]) state.orderBook.data[outcomeId] = {}
      if (!state.orderBook.data[outcomeId][currency_mode]) state.orderBook.data[outcomeId][currency_mode] = {}
      state.orderBook.data[outcomeId][currency_mode][position] = {
        status: constants.ERROR,
        error,
        ask: [],
        bid: [],
        last_price: null,
      }
    },
    clearOrderBook: (state, action) => {
      const { outcomeId, currency_mode, position } = action.payload
      if (currency_mode && outcomeId && position) {
        if (state.orderBook.data[outcomeId] && state.orderBook.data[outcomeId][currency_mode]) {
          delete state.orderBook.data[outcomeId][currency_mode][position]
        }
      } else if (currency_mode && outcomeId) {
        if (state.orderBook.data[outcomeId]) {
          delete state.orderBook.data[outcomeId][currency_mode]
        }
      } else if (outcomeId) {
        delete state.orderBook.data[outcomeId]
      } else {
        // Clear all if no specific outcome provided
        state.orderBook.data = {}
      }
    },

    userOrdersFetchStart: (state, action) => {
      const { marketId } = action.payload
      state.userOrders.status = constants.LOADING
      state.userOrders.data[marketId] = {
        ...(state.userOrders.data[marketId] || {}),
        isLoading: true,
        error: null,
      }
    },

    userOrdersFetchSuccess: (state, action) => {
      const { marketId, orders } = action.payload
      state.userOrders.status = constants.COMPLETE
      state.userOrders.data[marketId] = {
        orders,
        isLoading: false,
        error: null,
      }
    },

    userOrdersFetchError: (state, action) => {
      const { marketId, error } = action.payload
      state.userOrders.status = constants.ERROR
      state.userOrders.error = error
      state.userOrders.data[marketId] = {
        ...(state.userOrders.data[marketId] || {}),
        isLoading: false,
        error,
      }
    },

    clearUserOrders: (state, action) => {
      const { marketId } = action.payload
      delete state.userOrders.data[marketId]
    },
  },
})

export const {
  getFeedError,
  getFeedStart,
  getFeedSuccess,
  resetFeed,
  resetCategory,
  removeFromFeed,
  resetAllMarkets,
  getMarketsByTagStart,
  getMarketsByTagSuccess,
  getMarketsByTagError,
} = markets.actions
export const {
  getMarketByIdStart,
  getMarketByIdError,
  getMarketByIdSuccess,
  getRelatedMarketSuccess,
  getHotMarketSuccess,
} = markets.actions
export const { getMarketByCategoryIdStart, getMarketByCategoryIdSuccess, getMarketByCategoryIdError } = markets.actions
export const {
  updateMarketOutcomes,
  addBetToMarket,
  updateBetInMarket,
  overwriteBetsInMarket,
  updateMarket,
  updateMarketSportsEvent,
  addMarketsToPool,
} = markets.actions
export const { highlightMarkets, clearHighlightedMarkets } = markets.actions
export const {
  orderBookFetchStart,
  orderBookFetchSuccess,
  orderBookFetchError,
  clearOrderBook,
  userOrdersFetchStart,
  userOrdersFetchSuccess,
  userOrdersFetchError,
  clearUserOrders,
} = markets.actions
export default markets.reducer

// Selectors:

export const selectMarketRepository = state => state.markets.pool.data

export const selectFeedMarkets = state => state.markets.feed.data

export const selectFeedMarketsPagination = state => state.markets.feed.pagination

export const selectFeedMarketsError = state => state.markets.feed.error

export const selectFeedMarketsStatus = state => state.markets.feed.status

export const selectFeedMarketType = state => state.markets.feed.marketType

export const selectShouldLoadFeed =
  (marketType, queryString = '', currentCategory) =>
  state => {
    return (
      state.markets.feed.marketType !== marketType ||
      state.markets.feed.status === constants.IDLE ||
      state.markets.feed.currentCategory !== currentCategory ||
      state.markets.feed.queryString !== queryString
    )
  }

export const selectMarketById = marketId => state => state.markets.pool.data[marketId]
export const selectRelatedMarketById = () => state => state.markets.related_markets
export const selectHotMarket = () => state => state.markets.hot_markets

export const selectMarketStatusById = marketId => state => state.markets.pool.status[marketId]

export const selectMarketErrorById = marketId => state => state.markets.pool.error[marketId]

export const selectMarketsByCategoryId = categoryId => state => {
  const marketIds = state.markets.categories.data[categoryId]
  if (marketIds) {
    return marketIds.map(id => state.markets.pool.data[id])
  }
  return []
}

export const selectMarketsByTagName = tagName => state => {
  const marketIds = state.markets.tags.data[tagName]
  if (marketIds) {
    return marketIds.map(id => state.markets.pool.data[id])
  }
  return []
}
export const fetchMarketWagersByMarketId =
  (marketId, clear = false) =>
  async dispatch => {
    try {
      var response = await requestService.get(`/wagers/?question=` + marketId + '&limit=100')
    } catch (error) {
      return error
    }
    const data = await response.json()
    dispatch(overwriteBetsInMarket({ bets: data.results, marketId, clear }))
  }

export const selectMarketsStatusByTagName = tagName => state => state.markets.tags.status[tagName] || constants.IDLE

export const selectMarketsErrorByTagName = tagName => state => state.markets.tags.error[tagName]

export const selectMarketsPaginationByTagName = tagName => state => state.markets.tags.pagination[tagName]

export const selectMarketsStatusByCategoryId = categoryId => state =>
  state.markets.categories.status[categoryId] || constants.IDLE

export const selectMarketsErrorByCategoryId = categoryId => state => state.markets.categories.error[categoryId]

export const selectMarketsPaginationByCategoryId = categoryId => state =>
  state.markets.categories.pagination[categoryId]

export const selectHighlightedMarkets = state => state.markets.highlighted_markets

const buildOrderBookView = orderBookData => {
  if (!orderBookData || (!orderBookData.ask && !orderBookData.bid)) {
    return {
      buySeries: [],
      sellSeries: [],
      spread: 0,
      lastPrice: orderBookData?.last_price ?? null,
      status: orderBookData?.status || constants.IDLE,
      error: orderBookData?.error || null,
    }
  }

  // Use ask and bid arrays directly from the API response
  const buySeries = [...(orderBookData.ask || [])].reverse()
  const sellSeries = orderBookData.bid || []

  let spread = 0
  if (buySeries.length > 0 && sellSeries.length > 0) {
    const highestBid = buySeries[buySeries.length - 1].price
    const lowestAsk = sellSeries[0].price
    spread = Math.round(Math.abs(highestBid - lowestAsk) * 100000) / 100000
  }

  return {
    buySeries,
    sellSeries,
    spread,
    lastPrice: orderBookData.last_price ?? null,
    status: orderBookData.status,
    error: orderBookData.error,
  }
}

// The derived view is a new object on every call, so each (outcome, currency, position)
// triple keeps its own memoized selector instance and only recomputes when its slice changes.
const orderBookSelectorCache = new Map()

export const selectOrderBookByOutcomeId = (outcomeId, currency, position) => {
  const currencyType = currency === 'OOM' ? 'play_money' : 'real_money'
  const cacheKey = `${outcomeId}|${currencyType}|${position}`

  let selector = orderBookSelectorCache.get(cacheKey)
  if (!selector) {
    selector = createSelector(
      state => state.markets.orderBook.data[outcomeId]?.[currencyType]?.[position],
      buildOrderBookView
    )
    orderBookSelectorCache.set(cacheKey, selector)
  }

  return selector
}

// User Orders selectors
export const selectUserOrdersStatus = state => state.markets.userOrders.status
export const selectUserOrdersError = state => state.markets.userOrders.error
export const selectUserOrdersByMarketId = marketId => state =>
  state.markets.userOrders.data[marketId] || { orders: [], isLoading: false, error: null }

// Thunks:
export const fetchFeed =
  (marketType, options = {}, queryString = '', shouldResetFeed = false) =>
  async dispatch => {
    if (shouldResetFeed) {
      await dispatch(resetFeed())
    }

    /* The abort is for the browser, where a new category should cancel the previous
       list. On the server each request has its own store, and one visitor must not
       cancel another visitor's (or prewarm's) in-flight questions fetch. */
    const isServer = typeof window === 'undefined'
    if (!isServer) {
      currentFeedAbortController?.abort()
      currentFeedAbortController = new AbortController()
    }
    const signal = isServer ? undefined : currentFeedAbortController.signal
    const requestId = ++feedRequestCounter

    if (options.categories === '') {
      await dispatch(getFeedStart({ marketType, requestId }))
      await dispatch(
        getFeedSuccess({
          results: [],
          pagination: { total: 0, next: null, previous: null },
          queryString,
          currentCategory: '',
          marketType,
          replace: true,
          requestId,
        })
      )
      return
    }

    const params = requestService.parseFilters(options)
    params.set('currency_mode', marketType)
    await dispatch(getFeedStart({ marketType, requestId }))
    try {
      var response = await requestService.get(`/questions/?${params.toString()}`, { signal })
    } catch (err) {
      if (err?.reason_name === 'AbortError') return
      dispatch(getFeedError({ ...err, requestId }))
      return
    }

    const payload = await response.json()
    // First-page fetch (no offset or startIndex 0) replaces feed; load-more appends
    const replace = options.startIndex === undefined || options.startIndex === 0
    await dispatch(
      getFeedSuccess({
        ...payload,
        queryString,
        currentCategory: options.categories,
        marketType,
        replace,
        requestId,
      })
    )
  }

export const fetchMarketById =
  (marketId, marketType, silent = false) =>
  async (dispatch, getState) => {
    // A non-silent fetch already in flight owns this id; siblings mounted in the
    // same commit read a stale status from their own render and would refetch.
    if (getState().markets.pool.status[marketId] === constants.LOADING) return
    if (!silent) {
      dispatch(getMarketByIdStart(marketId))
    }
    const params = requestService.parseFilters({})
    params.set('currency_mode', marketType)
    try {
      var response = await requestService.get(`/questions/${marketId}/?${params.toString()}`)
    } catch (error) {
      dispatch(getMarketByIdError({ marketId, error }))
      return
    }
    const data = await response.json()
    dispatch(getMarketByIdSuccess({ data }))
    return data
  }
export const fetchRelatedMarketById = marketId => async dispatch => {
  try {
    var response = await requestService.get(`/questions/${marketId}/related_markets/`)
  } catch (error) {
    dispatch(getMarketByIdError({ marketId, error }))
    return
  }
  if (response) {
    const data = await response.json()
    dispatch(getRelatedMarketSuccess({ data }))
    return data
  }
}

export const fetchMarkets = async (marketType, options) => {
  const currencyParam = getMarketType(marketType === constants.REAL_MONEY)
  const params = requestService.parseFilters(options)
  params.set('currency_mode', currencyParam)
  return requestService.get(`/questions/?${params.toString()}`)
}

let hotMarketsRequestSeq = 0

export const fetchHotMarkets = options => async dispatch => {
  const requestSeq = ++hotMarketsRequestSeq
  const params = requestService.parseFilters(options)

  try {
    var response = await requestService.get(`/questions/hot/?${params.toString()}`)
  } catch (error) {
    return error
  }
  // A slower response for the previous category must not replace the list the
  // user just asked for. Only the latest hot-markets request is applied.
  if (requestSeq !== hotMarketsRequestSeq) return
  if (response) {
    const data = await response.json()
    dispatch(getHotMarketSuccess({ data }))
    return data
  }
}
export const followOrUnfollow = marketId => async dispatch => {
  try {
    var response = await requestService.post(`/questions/${marketId}/toggle_follow/`)
  } catch (err) {
    return { error: true }
  }

  const data = await response.json()
  await dispatch(updateMarket({ market: { id: marketId, is_following: data.is_following } }))
  return { error: false }
}
export const fetchMarketsByCategoryId =
  (categoryId, marketType, options = {}) =>
  async dispatch => {
    dispatch(getMarketByCategoryIdStart({ categoryId }))
    try {
      var response = await fetchMarkets(marketType, { ...options, categories: categoryId })
    } catch (error) {
      dispatch(getMarketByCategoryIdError({ categoryId, error }))
      throw error
    }
    const { results, pagination } = await response.json()
    dispatch(getMarketByCategoryIdSuccess({ categoryId, marketsList: results, pagination }))
  }

/**
 * Every market of a category whatever its stage: the default feed only lists
 * wagerable markets, so the ones closed for betting (`pending_resolution`) and
 * the resolved ones (`resolved_only`) come from their own requests. Stored as
 * one deduplicated list, in that order. Only the open request is required: a
 * failed closed or resolved request leaves those markets out instead of
 * failing the category. `limit` applies to each request, and no pagination is
 * stored since the merged list spans three pages.
 */
export const fetchAllMarketsByCategoryId =
  (categoryId, marketType, options = {}) =>
  async dispatch => {
    dispatch(getMarketByCategoryIdStart({ categoryId }))
    const stages = [{}, { pending_resolution: true }, { resolved_only: true }]
    const pages = await Promise.allSettled(
      stages.map(async stage => {
        const response = await fetchMarkets(marketType, { ...options, ...stage, categories: categoryId })
        if (!response.ok) throw new Error(`server error: ${response.status}`)
        return response.json()
      })
    )
    if (pages[0].status === 'rejected') {
      dispatch(getMarketByCategoryIdError({ categoryId, error: pages[0].reason }))
      throw pages[0].reason
    }
    const seen = new Set()
    const marketsList = pages
      .flatMap(page => (page.status === 'fulfilled' ? page.value?.results || [] : []))
      .filter(market => !seen.has(market.id) && seen.add(market.id))
    dispatch(getMarketByCategoryIdSuccess({ categoryId, marketsList, pagination: undefined }))
  }

export const fetchTagsByName =
  (tagName, marketType, options = {}) =>
  async dispatch => {
    dispatch(getMarketsByTagStart({ tag: tagName }))
    try {
      var response = await fetchMarkets(marketType, { ...options, tag: tagName })
    } catch (error) {
      dispatch(getMarketsByTagError({ tag: tagName, error }))
      throw error
    }
    const { results, pagination } = await response.json()
    dispatch(getMarketsByTagSuccess({ tag: tagName, marketsList: results, pagination }))
  }

export const resetCategoryMarkets = categoryId => dispatch => {
  dispatch(resetCategory({ categoryId }))
}

export const fetchAndHighlightMarkets = marketIds => async (dispatch, getState) => {
  // Fetch each market individually
  for (const marketId of marketIds) {
    try {
      await dispatch(fetchMarketById(marketId, getState().markets.feed.marketType, true))
    } catch (error) {
      console.error(`Error fetching market ${marketId}:`, error)
    }
  }

  // Highlight the markets
  dispatch(highlightMarkets({ marketIds }))
}

/**
 * Fetch order book for a market
 * @param {Object} options - { question, currency_mode, outcome, position, source, showErrorToast }
 * @param {string} [options.source] - Call-site label reported to Mixpanel on failure
 * @param {boolean} [options.showErrorToast] - Show the fail toast; only for user-initiated fetches
 * @returns {Function}
 */
export const fetchOrderBook =
  ({ question, currency_mode, outcome, position, source, showErrorToast = false }) =>
  async dispatch => {
    dispatch(orderBookFetchStart({ outcomeId: outcome, currency_mode, marketId: question, position }))
    try {
      const response = await limitOrderAPI.getAll(question, { currency_mode, outcome, position })

      // API returns { ask, bid, last_price }. last_price is the latest settled trade.
      const ask = response.ask || []
      const bid = response.bid || []
      const last_price = response.last_price ?? null

      // Validate data integrity
      const allOrders = [...ask, ...bid]
      const invalidOrders = allOrders.filter(order => !order.price)
      if (invalidOrders.length > 0) {
        ErrorService.captureError(new Error('Order book contains orders missing a price'), {
          component: 'fetchOrderBook',
          type: 'order_book_invalid_orders',
          context: {
            questionId: question,
            outcomeId: outcome,
            currency_mode,
            position,
            source,
            invalid_count: invalidOrders.length,
          },
        })
      }

      dispatch(orderBookFetchSuccess({
        outcomeId: outcome,
        currency_mode,
        ask,
        bid,
        last_price,
        position,
      }))
    } catch (error) {
      dispatch(orderBookFetchError({ outcomeId: outcome, currency_mode, error, position }))
      if (!ErrorService.isIgnorableNetworkRejection(error)) {
        ErrorService.captureError(error, {
          component: 'fetchOrderBook',
          type: 'order_book_fetch_error',
          context: {
            questionId: question,
            outcomeId: outcome,
            currency_mode,
            position,
            source,
            status: error?.status,
            request_url: error?.url,
            failure_reason: error?.reason,
            failure_reason_name: error?.reason_name,
          },
        })
      }
      if (showErrorToast) {
        toastService('fail', {
          title: i18n('api_errors.titles.order_book'),
          body: i18n('api_errors.order_book_unavailable'),
        })
      }
    }
  }
